import React, { useState, useEffect } from 'react'
import { Badge } from 'components/badge'
import { Divider } from 'components/divider'
import { ErrorDialog } from 'components/custom/ResponseDialog'
import { useTranslation } from 'react-i18next'
import { customDateFormat, getS3UrlFromKey } from 'utils/common'
import {
    ArrowLeftIcon,
    ArrowDownTrayIcon,
    EyeIcon,
    PhoneIcon,
    ChatBubbleBottomCenterIcon,
    UserIcon,
    DocumentIcon,
} from '@heroicons/react/20/solid'
import { UsersIcon, PassportIcon, MedicalIcon } from 'components/icons'
import stSeat from '../../../assets/images/icons/st_seat.svg'
import vipSeat from '../../../assets/images/icons/vip_seat.svg'
import { GET_TRIP_PASSENGERS } from 'graphql/operations/queries'
import loadingImage from '../../../assets/images/boat-loading.gif'
import { useLazyQuery, useMutation } from '@apollo/client'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { CANCEL_BOOKING } from 'graphql/booking/mutations'
import { SuccessDialog, ConfirmDialog } from 'components/custom/ResponseDialog'
import { XCircleIcon } from '@heroicons/react/24/outline'

export default function TripPassengerList() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { trip_id } = useParams()
    const location = useLocation()
    const { trip_date, trip_name } = location.state || {}

    const [showErrDialog, setShowErrDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [passengerList, setPassengerList] = useState([])
    const [crewList, setCrewList] = useState([])
    const [showDocumentPreview, setShowDocumentPreview] = useState(false)
    const [previewDocument, setPreviewDocument] = useState(null)
    const [documentUrl, setDocumentUrl] = useState(null)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [selectedBookingForCancel, setSelectedBookingForCancel] = useState(null)

    // Get Trip Passengers
    const [getTripPassengers, { loading: passengersLoading, error: passengersError }] = useLazyQuery(
        GET_TRIP_PASSENGERS,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.getTripPassengers.success && data.getTripPassengers.code === '200') {
                    setPassengerList(data.getTripPassengers.passengers || [])
                    setCrewList(data.getTripPassengers.crew_details || [])
                } else {
                    setShowErrDialog(true)
                    setErrorMessage(data.getTripPassengers.message || t('Failed to fetch passengers'))
                }
            },
            onError: error => {
                setShowErrDialog(true)
                setErrorMessage(error.message)
            },
        }
    )

    useEffect(() => {
        if (trip_id && trip_date) {
            getTripPassengers({
                variables: {
                    tripId: trip_id,
                    tripDate: trip_date,
                },
            })
        }
    }, [trip_id, trip_date])

    const handleErrClose = () => {
        setShowErrDialog(false)
        setErrorMessage('')
    }

    const handleSucClose = () => {
        setShowSucDialog(false)
        setSuccessMessage('')
    }

    // Cancel Booking Mutation
    const [cancelBooking, { loading: cancelLoading }] = useMutation(CANCEL_BOOKING, {
        onCompleted: data => {
            if (data?.cancelBooking.code === '200' && data?.cancelBooking.success === true) {
                setSuccessMessage(data?.cancelBooking?.message || t('Booking cancelled successfully'))
                setShowSucDialog(true)
                // Refresh passenger list
                getTripPassengers({
                    variables: {
                        tripId: trip_id,
                        tripDate: trip_date,
                    },
                })
            } else {
                setErrorMessage(data?.cancelBooking?.message || t('Failed to cancel booking'))
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to cancel booking'))
            setShowErrDialog(true)
        },
    })

    const handleCancelBooking = (bookingId, tripId) => {
        setSelectedBookingForCancel({ bookingId, tripId })
        setShowConfirmDialog(true)
    }

    const confirmCancelBooking = () => {
        if (selectedBookingForCancel) {
            cancelBooking({
                variables: {
                    bookingId: selectedBookingForCancel.bookingId,
                    tripIds:[],
                    cancellationType: 'FULL',
                },
            })
        }
        setShowConfirmDialog(false)
        setSelectedBookingForCancel(null)
    }

    // Group passengers by booking code
    const groupedPassengers = passengerList.reduce((acc, passenger) => {
        const bookingCode = passenger.booking_code
        if (!acc[bookingCode]) {
            acc[bookingCode] = {
                bookingCode,
                bookingId: passenger.booking_id,
                tripId: passenger.trip_id,
                travelDate: passenger.travel_date,
                passengers: [],
            }
        }
        acc[bookingCode].passengers.push(passenger)
        return acc
    }, {})

    const bookingGroups = Object.values(groupedPassengers)
    const totalBookings = bookingGroups.length

    const handleViewDocument = async (doc) => {
        const url = await getS3UrlFromKey(doc.s3_key)
        if (url) {
            setPreviewDocument(doc)
            setDocumentUrl(url.toString())
            setShowDocumentPreview(true)
        }
    }

    const handleDownloadDocument = async (doc) => {
        try {
            const url = await getS3UrlFromKey(doc.s3_key)
            if (url) {
                // Fetch the file as blob to force download
                const response = await fetch(url.toString())
                const blob = await response.blob()
                const blobUrl = window.URL.createObjectURL(blob)
                
                const link = document.createElement('a')
                link.href = blobUrl
                link.download = doc.file_name || 'document'
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                
                // Clean up the blob URL
                window.URL.revokeObjectURL(blobUrl)
            }
        } catch (error) {
            console.error('Error downloading document:', error)
            setShowErrDialog(true)
            setErrorMessage(t('Failed to download document'))
        }
    }

    const formatDocumentType = (docType) => {
        const types = {
            'NID': t('National ID'),
            'PSP': t('Passport'),
            'VISA': t('Visa'),
        }
        return types[docType] || docType
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => {
                        navigate(-1)
                    }}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Back')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between">
                        <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap mb-4">
                            <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                                    <UsersIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                </div>
                                <div>
                                    <span className="ms-3">{t('Trip Manifest')}</span>
                                    <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                        {trip_name && `${trip_name} - `}
                                        {trip_date && customDateFormat(trip_date)}
                                    </p>
                                </div>
                            </h1>
                        </div>
                    </div>
                </div>
            </div>
            <Divider />

            {/* Summary Statistics */}
            {!passengersLoading && !passengersError && passengerList.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6 mt-6">
                    {/* Total Passengers Card */}
                    <div className="bg-white p-5 rounded-lg shadow-lg border border-gray-100 flex items-center justify-between hover:shadow-xl transition-shadow duration-200">
                        <div>
                            <h3 className="text-gray-600 text-sm font-medium mb-1">{t('Total Passengers')}</h3>
                            <p className="text-3xl font-bold text-primary">{passengerList.length}</p>
                        </div>
                        <div className="bg-primary/10 p-4 rounded-xl flex items-center justify-center">
                            <UsersIcon className="w-7 h-7 fill-primary/90" />
                        </div>
                    </div>

                    {/* Total Bookings Card */}
                    <div className="bg-white p-5 rounded-lg shadow-lg border border-gray-100 flex items-center justify-between hover:shadow-xl transition-shadow duration-200">
                        <div>
                            <h3 className="text-gray-600 text-sm font-medium mb-1">{t('Total Bookings')}</h3>
                            <p className="text-3xl font-bold text-primary">{totalBookings}</p>
                        </div>
                        <div className="bg-green/10 p-4 rounded-xl flex items-center justify-center">
                            <UserIcon className="w-7 h-7 text-green/90" />
                        </div>
                    </div>

                    {/* Crew Members Card - Only show if crew exists */}
                    {crewList.length > 0 && (
                        <div className="bg-white p-5 rounded-lg shadow-lg border border-gray-100 flex items-center justify-between hover:shadow-xl transition-shadow duration-200">
                            <div>
                                <h3 className="text-gray-600 text-sm font-medium mb-1">{t('Crew Members')}</h3>
                                <p className="text-3xl font-bold text-primary">{crewList.length}</p>
                            </div>
                            <div className="bg-purple-600/10 p-4 rounded-xl flex items-center justify-center">
                                <UserIcon className="w-7 h-7 text-purple-600/90" />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {passengersLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">{t('Loading, please wait')}...</p>
                </div>
            )}

            {passengersError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch passengers!')}</div>
                </div>
            )}

            {!passengersLoading && !passengersError && passengerList.length === 0 && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No passengers found!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('No passengers for this trip')}</p>
                </div>
            )}

            {!passengersLoading && !passengersError && passengerList.length > 0 && (
                <>
                    {/* Crew Details Section */}
                    {crewList.length > 0 && (
                        <div className="mb-6">
                            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <UserIcon className="w-5 h-5 text-purple-600" />
                                {t('Crew Members')}
                            </h2>
                            <div className="bg-white rounded-lg border border-gray-300 shadow-sm overflow-hidden">
                                <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-4 py-2.5">
                                    <h3 className="text-white font-bold text-base">
                                        {crewList.length} {crewList.length === 1 ? t('Crew Member') : t('Crew Members')}
                                    </h3>
                                </div>
                                <div className="divide-y divide-gray-200">
                                    {crewList.map((crew, index) => (
                                        <div key={crew.id || index} className="p-3 hover:bg-gray-50 transition-colors">
                                            {/* Crew Header */}
                                            <div className="flex items-start justify-between mb-2">
                                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                                    <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                                                        <UserIcon className="w-4 h-4 text-white" />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <h4 className="text-sm font-bold text-gray-900 truncate">
                                                            {crew.user_info?.given_name} {crew.user_info?.family_name}
                                                        </h4>
                                                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                                            {crew.additional_info?.job_title && (
                                                                <Badge color="purple" className="text-[10px] py-0.5 px-1.5">
                                                                    {crew.additional_info.job_title}
                                                                </Badge>
                                                            )}
                                                            {crew.user_roles && crew.user_roles.map((role, idx) => (
                                                                <Badge key={idx} color="sky" className="text-[10px] py-0.5 px-1.5 capitalize">
                                                                    {role.replace(/_/g, ' ')}
                                                                </Badge>
                                                            ))}
                                                            <Badge
                                                                color={crew.is_active ? 'green' : 'red'}
                                                                className="text-[10px] py-0.5 px-1.5"
                                                            >
                                                                {crew.is_active ? t('Active') : t('Inactive')}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Contact Info */}
                                            <div className="grid grid-cols-2 gap-2 mb-2">
                                                <div className="flex items-center gap-1.5 text-xs">
                                                    <PhoneIcon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                                                    <span className="text-gray-700 truncate">{crew.user_info?.phone_number || <span className="italic text-gray-400">N/A</span>}</span>
                                                </div>
                                                <div className="flex items-center gap-1.5 text-xs">
                                                    <ChatBubbleBottomCenterIcon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                                                    <span className="text-gray-700 truncate">{crew.email || <span className="italic text-gray-400">N/A</span>}</span>
                                                </div>
                                            </div>

                                            {/* Document Information */}
                                            {crew.additional_info?.document_type && (
                                                <div className="mb-2">
                                                    <div className="flex items-center gap-1.5 text-xs">
                                                        <PassportIcon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                                                        <span className="text-gray-600 font-medium">
                                                            {crew.additional_info.document_type.description?.en}: 
                                                        </span>
                                                        <span className="text-gray-700">
                                                            {crew.additional_info.document_number || <span className="italic text-gray-400">N/A</span>}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Crew Documents */}
                                            {crew.additional_info?.document && (
                                                <div className="border-t border-gray-200 pt-2 mt-2">
                                                    <div className="flex items-center gap-1 mb-1.5">
                                                        <DocumentIcon className="w-3 h-3 text-gray-500" />
                                                        <p className="text-[10px] font-semibold text-gray-600 uppercase">{t('Primary Document')}</p>
                                                    </div>
                                                    <div className="flex items-center gap-2 p-1.5 bg-gray-50 rounded border border-gray-200 hover:border-primary/30 transition-colors">
                                                        <PassportIcon className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                                                        <div className="min-w-0 flex-1">
                                                            <p className="text-xs font-medium text-gray-900 truncate">{crew.additional_info.document.file_name}</p>
                                                            <p className="text-[10px] text-gray-500">{(crew.additional_info.document.size / 1024).toFixed(2)} KB</p>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <button onClick={() => handleViewDocument(crew.additional_info.document)} className="p-1 text-white bg-primary hover:bg-primary/90 rounded" title={t('View')}>
                                                                <EyeIcon className="h-3.5 w-3.5" />
                                                            </button>
                                                            <button onClick={() => handleDownloadDocument(crew.additional_info.document)} className="p-1 text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded" title={t('Download')}>
                                                                <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Additional Attachments */}
                                            {crew.additional_info?.attachments && crew.additional_info.attachments.length > 0 && (
                                                <div className="border-t border-gray-200 pt-2 mt-2">
                                                    <div className="flex items-center gap-1 mb-1.5">
                                                        <DocumentIcon className="w-3 h-3 text-gray-500" />
                                                        <p className="text-[10px] font-semibold text-gray-600 uppercase">{t('Additional Documents')}</p>
                                                    </div>
                                                    <div className="space-y-1">
                                                        {crew.additional_info.attachments.map((attachment, attIndex) => (
                                                            <div key={attIndex}>
                                                                <div className="text-[10px] text-gray-600 mb-0.5">
                                                                    {attachment.document_type}: {attachment.document_number}
                                                                </div>
                                                                {attachment.document && (
                                                                    <div className="flex items-center gap-2 p-1.5 bg-gray-50 rounded border border-gray-200 hover:border-primary/30 transition-colors">
                                                                        <PassportIcon className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                                                                        <div className="min-w-0 flex-1">
                                                                            <p className="text-xs font-medium text-gray-900 truncate">{attachment.document.file_name}</p>
                                                                            <p className="text-[10px] text-gray-500">{(attachment.document.size / 1024).toFixed(2)} KB</p>
                                                                        </div>
                                                                        <div className="flex items-center gap-1">
                                                                            <button onClick={() => handleViewDocument(attachment.document)} className="p-1 text-white bg-primary hover:bg-primary/90 rounded" title={t('View')}>
                                                                                <EyeIcon className="h-3.5 w-3.5" />
                                                                            </button>
                                                                            <button onClick={() => handleDownloadDocument(attachment.document)} className="p-1 text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded" title={t('Download')}>
                                                                                <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Passengers Section */}
                    <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <UsersIcon className="w-5 h-5 fill-primary" />
                        {t('Passengers')}
                    </h2>
                    <div className="space-y-4">
                        {bookingGroups.map((group, groupIndex) => (
                            <div key={group.bookingCode} className="bg-white rounded-lg border border-gray-300 shadow-sm overflow-hidden">
                                {/* Booking Group Header */}
                                <div className="bg-gradient-to-r from-[#4a7c8f] to-[#5a8c9f] px-4 py-2.5 flex items-center justify-between">
                                    <div className="flex items-center gap-2.5">
                                        <div className="bg-white/20 rounded-full p-1.5">
                                            <UserIcon className="w-4 h-4 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-white font-bold text-base">
                                                {t('Booking')}: {group.bookingCode}
                                            </h3>
                                            <p className="text-white/90 text-xs">
                                                {group.passengers.length} {group.passengers.length === 1 ? t('passenger') : t('passengers')}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleCancelBooking(group.bookingId, group.tripId)}
                                        disabled={cancelLoading}
                                        className="ml-auto self-center inline-flex items-center gap-1.5 h-9 px-3 bg-red hover:bg-red-700 disabled:bg-red-400 text-white text-sm font-semibold rounded-md shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 flex-shrink-0 whitespace-nowrap"
                                        title={t('Cancel Booking')}
                                    >
                                        <XCircleIcon className="w-4 h-4" />
                                        {cancelLoading ? t('Cancelling...') : t('Cancel Booking')}
                                    </button>
                                </div>

                                {/* Passengers List */}
                                <div className="divide-y divide-gray-200">
                                    {group.passengers.map((person, index) => (
                                        <div key={person.passenger_id || index} className="p-3 hover:bg-gray-50 transition-colors">
                                            {/* Passenger Header */}
                                            <div className="flex items-start justify-between mb-2">
                                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                                                        <UserIcon className="w-4 h-4 text-white" />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <h4 className="text-sm font-bold text-gray-900 truncate">
                                                            {person.passenger?.given_name} {person.passenger?.family_name}
                                                        </h4>
                                                        <div className="flex items-center gap-1.5 mt-0.5">
                                                            <Badge color="sky" className="text-[10px] py-0.5 px-1.5">
                                                                {person.age_group?.description?.en || 'Adult'}
                                                            </Badge>
                                                            {person.counter_status && (
                                                                <Badge
                                                                    color={
                                                                        person.counter_status.code === 'CHI'
                                                                            ? 'green'
                                                                            : person.counter_status.code === 'CRT'
                                                                            ? 'yellow'
                                                                            : 'gray'
                                                                    }
                                                                    className="text-[10px] py-0.5 px-1.5"
                                                                >
                                                                    {person.counter_status.description?.en || person.counter_status.code}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 rounded border border-amber-200">
                                                        <img src={person.seat_type === 'VIP' ? vipSeat : stSeat} className="w-4 h-4" alt={person.seat_type} />
                                                        <div className="w-6 h-6 bg-white rounded flex items-center justify-center border border-amber-400">
                                                            <span className="text-xs font-bold text-amber-700">{person.seat_no}</span>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-[10px] text-gray-500">{t('Ticket No')}</p>
                                                        <p className="text-xs font-mono font-bold text-primary">
                                                            {person.ticket_number}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Contact Info */}
                                            <div className="grid grid-cols-2 gap-2 mb-2">
                                                <div className="flex items-center gap-1.5 text-xs">
                                                    <PhoneIcon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                                                    <span className="text-gray-700 truncate">{person.passenger?.phone_number || <span className="italic text-gray-400">N/A</span>}</span>
                                                </div>
                                                <div className="flex items-center gap-1.5 text-xs">
                                                    <ChatBubbleBottomCenterIcon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                                                    <span className="text-gray-700 truncate">{person.passenger?.email || <span className="italic text-gray-400">N/A</span>}</span>
                                                </div>
                                            </div>

                                            {/* Alerts */}
                                            {(person.passenger?.special_needs || person.passenger?.medical_condition) && (
                                                <div className="flex gap-2 mb-2">
                                                    {person.passenger?.special_needs && (
                                                        <div className="flex-1 px-2 py-1 bg-amber-50 rounded border-l-2 border-amber-400">
                                                            <div className="flex items-center gap-1">
                                                                <svg className="h-3 w-3 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                                                </svg>
                                                                <p className="text-[10px] font-semibold text-amber-900">{t('Special Needs')}</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {person.passenger?.medical_condition && (
                                                        <div className="flex-1 px-2 py-1 bg-red-50 rounded border-l-2 border-red-400">
                                                            <div className="flex items-center gap-1">
                                                                <MedicalIcon className="h-3 w-3 flex-shrink-0" />
                                                                <p className="text-[10px] font-semibold text-red-900">{t('Medical Condition')}</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Documents */}
                                            {person.passenger?.document && person.passenger.document.length > 0 && (
                                                <div className="border-t border-gray-200 pt-2">
                                                    <div className="flex items-center gap-1 mb-1.5">
                                                        <DocumentIcon className="w-3 h-3 text-gray-500" />
                                                        <p className="text-[10px] font-semibold text-gray-600 uppercase">{t('Documents')}</p>
                                                    </div>
                                                    <div className="space-y-1">
                                                        {person.passenger.document.map((doc, docIndex) => (
                                                            <div key={docIndex} className="flex items-center gap-2 p-1.5 bg-gray-50 rounded border border-gray-200 hover:border-primary/30 transition-colors">
                                                                <PassportIcon className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                                                                <div className="min-w-0 flex-1">
                                                                    <p className="text-xs font-medium text-gray-900 truncate">{doc.file_name}</p>
                                                                    <p className="text-[10px] text-gray-500">{(doc.size / 1024).toFixed(2)} KB</p>
                                                                </div>
                                                                <div className="flex items-center gap-1">
                                                                    <button onClick={() => handleViewDocument(doc)} className="p-1 text-white bg-primary hover:bg-primary/90 rounded" title={t('View')}>
                                                                        <EyeIcon className="h-3.5 w-3.5" />
                                                                    </button>
                                                                    <button onClick={() => handleDownloadDocument(doc)} className="p-1 text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded" title={t('Download')}>
                                                                        <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={errorMessage} onClose={handleErrClose} />
            )}

            {showSucDialog && (
                <SuccessDialog status={true} title={t('Success')} message={successMessage} onClose={handleSucClose} />
            )}

            {showConfirmDialog && (
                <ConfirmDialog
                    status={true}
                    title={t('Cancel Booking')}
                    message={t('Are you sure you want to cancel this booking? This action cannot be undone.')}
                    negativeActionText={t('No, Keep Booking')}
                    positiveActionText={t('Yes, Cancel Booking')}
                    onPositive={confirmCancelBooking}
                    onNegative={() => {
                        setShowConfirmDialog(false)
                        setSelectedBookingForCancel(null)
                    }}
                />
            )}

            {/* Document Preview Dialog */}
            {showDocumentPreview && previewDocument && (
                <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        {/* Background overlay */}
                        <div
                            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                            aria-hidden="true"
                            onClick={() => setShowDocumentPreview(false)}
                        ></div>

                        {/* Modal panel */}
                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                            {/* Header */}
                            <div className="bg-white px-4 py-4 border-b border-gray-200">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                        <PassportIcon className="w-6 h-6 text-primary flex-shrink-0" />
                                        <div className="min-w-0 flex-1">
                                            <h3 className="text-lg font-medium text-gray-900" id="modal-title">
                                                {t('Document Preview')}
                                            </h3>
                                            <p className="text-sm text-gray-500 truncate max-w-md">
                                                {previewDocument.file_name}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleDownloadDocument(previewDocument)}
                                            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                        >
                                            <ArrowDownTrayIcon className="h-4 w-4" />
                                            {t('Download')}
                                        </button>
                                        <button
                                            onClick={() => setShowDocumentPreview(false)}
                                            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                        >
                                            {t('Close')}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="bg-gray-100 px-4 py-6" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                                {previewDocument.mime_type?.startsWith('image/') ? (
                                    <div className="flex justify-center">
                                        <img
                                            src={documentUrl}
                                            alt={previewDocument.file_name}
                                            className="max-w-full h-auto rounded shadow-lg"
                                        />
                                    </div>
                                ) : previewDocument.mime_type === 'application/pdf' ? (
                                    <iframe
                                        src={documentUrl}
                                        className="w-full rounded shadow-lg"
                                        style={{ height: '65vh' }}
                                        title={previewDocument.file_name}
                                    />
                                ) : (
                                    <div className="text-center py-12">
                                        <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
                                        <p className="mt-2 text-sm text-gray-500">
                                            {t('Preview not available for this file type. Please download to view.')}
                                        </p>
                                        <div className="mt-6">
                                            <button
                                                onClick={() => handleDownloadDocument(previewDocument)}
                                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                                style={{ backgroundColor: 'rgb(40 95 116 / var(--tw-bg-opacity, 1))' }}
                                            >
                                                <ArrowDownTrayIcon className="h-4 w-4" />
                                                {t('Download Document')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
